import { CompanyNode, LayoutDirection, PercentageDisplayMode } from '../types';

/**
 * Represents a snapshot of the application state that can be restored
 */
export interface HistoryState {
  companyNodes: CompanyNode[];
  nodePositions: Record<string, { x: number; y: number }>;
  layout: LayoutDirection;
  maxDepth: number | 'all';
  showCountryFlags: boolean;
  percentageDisplayMode?: PercentageDisplayMode;
}

/**
 * Custom hook for managing undo/redo history
 */
export class HistoryManager {
  private history: HistoryState[] = [];
  private currentIndex: number = -1;
  private maxHistorySize: number = 50;

  /**
   * Add a new state to history (clears any future states if we're in the middle of history)
   */
  push(state: HistoryState): void {
    // Remove any future states if we're not at the end
    if (this.currentIndex < this.history.length - 1) {
      this.history = this.history.slice(0, this.currentIndex + 1);
    }

    // Add new state
    this.history.push(this.deepClone(state));
    this.currentIndex++;

    // Limit history size
    if (this.history.length > this.maxHistorySize) {
      this.history.shift();
      this.currentIndex--;
    }
  }

  /**
   * Undo - go back one step
   */
  undo(): HistoryState | null {
    if (this.canUndo()) {
      this.currentIndex--;
      return this.deepClone(this.history[this.currentIndex]);
    }
    return null;
  }

  /**
   * Redo - go forward one step
   */
  redo(): HistoryState | null {
    if (this.canRedo()) {
      this.currentIndex++;
      return this.deepClone(this.history[this.currentIndex]);
    }
    return null;
  }

  /**
   * Check if undo is possible
   */
  canUndo(): boolean {
    return this.currentIndex > 0;
  }

  /**
   * Check if redo is possible
   */
  canRedo(): boolean {
    return this.currentIndex < this.history.length - 1;
  }

  /**
   * Clear all history
   */
  clear(): void {
    this.history = [];
    this.currentIndex = -1;
  }

  /**
   * Get current state
   */
  getCurrent(): HistoryState | null {
    if (this.currentIndex >= 0 && this.currentIndex < this.history.length) {
      return this.deepClone(this.history[this.currentIndex]);
    }
    return null;
  }

  /**
   * Initialize history with a starting state
   */
  initialize(state: HistoryState): void {
    this.history = [this.deepClone(state)];
    this.currentIndex = 0;
  }

  /**
   * Deep clone a state object
   */
  private deepClone(state: HistoryState): HistoryState {
    return {
      companyNodes: state.companyNodes.map(node => ({
        ...node,
        customization: node.customization ? { ...node.customization } : undefined,
        shareholders: node.shareholders.map(sh => ({ ...sh })),
        ownershipInfo: node.ownershipInfo,
        parentIds: node.parentIds ? [...node.parentIds] : undefined,
      })),
      nodePositions: { ...state.nodePositions },
      layout: state.layout,
      maxDepth: state.maxDepth,
      showCountryFlags: state.showCountryFlags,
      percentageDisplayMode: state.percentageDisplayMode,
    };
  }

  /**
   * Get history info for debugging
   */
  getHistoryInfo(): { total: number; current: number; canUndo: boolean; canRedo: boolean } {
    return {
      total: this.history.length,
      current: this.currentIndex + 1,
      canUndo: this.canUndo(),
      canRedo: this.canRedo(),
    };
  }
}

